import { createColumnHelper } from '@tanstack/react-table';
import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { trpc } from '../../../../trpc/react';
import { Gallery } from '../../../components/gallery/gallery';
import { FilterOption } from '../../../components/table/data-table-context';
import { useViewer } from '../../../context/viewer';
import { useFilteredGalleryPagination } from '../../../hooks/useFilteredGalleryPagination';
import { useToast } from '../../../hooks/useToast';
import { LibraryExercise } from '../exercises/types';
import { CreateExerciseMaxConversionSheet } from './components/CreateMaxConversionSheet';
import { EditExerciseMaxConversionSheet } from './components/EditMaxConversionSheet';
import { MaxConversionCard } from './components/MaxConversionCard';
import { LibraryExerciseMaxConversion } from './types';
import {
  ExerciseMaxConversionFormType,
  ratioFromPercentage,
  ratioToPercentage,
} from './utils';

const columnHelper = createColumnHelper<LibraryExerciseMaxConversion>();
const routeApi = getRouteApi(
  '/orgAuth/orgWithNav/$teamId/library/max-conversions',
);

export function MaxConversionsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { teamId } = routeApi.useParams();
  const {
    org: {
      settings: { enableTeamLibrary },
    },
    hasPermissions,
  } = useViewer();
  const enableCreate = hasPermissions(
    'library.exerciseMaxConversions',
    'create',
  );
  const selectedLibraryTeamId = teamId || undefined;
  const [fromExerciseFilter, setFromExerciseFilter] = useState<
    LibraryExercise[]
  >([]);

  // Fetch all exercises for the filter and sheets
  const allExercisesResult = trpc.exerciseLibrary.exerciseSearch.useQuery({
    teamId: selectedLibraryTeamId,
    includeBaseLibraryExercisesOverride: enableTeamLibrary,
  });

  const exercises = useMemo(
    () => allExercisesResult.data?.records ?? [],
    [allExercisesResult.data?.records],
  );

  const {
    refetch,
    data,
    pageSize,
    setPageSize,
    page,
    setPage,
    viewMode,
    setViewMode,
    isLoading,
  } = useFilteredGalleryPagination({
    queryFn:
      trpc.exerciseMaxConversionLibrary.exerciseMaxConversionSearch.useQuery,
    queryInput: {
      searchTerm: '',
      fromLibraryExerciseId:
        fromExerciseFilter.length > 0 ? fromExerciseFilter[0]?.id : undefined,
    },
  });

  const createMaxConversion =
    trpc.exerciseMaxConversionLibrary.createExerciseMaxConversion.useMutation({
      onSuccess: () => {
        refetch();
      },
    });

  const editMaxConversion =
    trpc.exerciseMaxConversionLibrary.editExerciseMaxConversion.useMutation({
      onSuccess: () => {
        refetch();
      },
    });

  const deleteMaxConversion =
    trpc.exerciseMaxConversionLibrary.deleteExerciseMaxConversion.useMutation({
      onSuccess: () => {
        refetch();
      },
    });

  const onCreateMaxConversion = useCallback(
    ({
      toLibraryExerciseId,
      fromLibraryExerciseId,
      percentage,
    }: ExerciseMaxConversionFormType) => {
      const id = v4();
      createMaxConversion.mutate(
        {
          id,
          toLibraryExerciseId,
          fromLibraryExerciseId,
          ratio: ratioFromPercentage(percentage),
        },
        {
          onSuccess: () =>
            toast({ title: t('conversionCreated'), severity: 'success' }),
          onError: (error) =>
            toast({ title: error.message, severity: 'error' }),
        },
      );
    },
    [createMaxConversion, t, toast, selectedLibraryTeamId],
  );

  const onEditMaxConversion = useCallback(
    (id: string, { percentage }: ExerciseMaxConversionFormType) => {
      editMaxConversion.mutate(
        { id, ratio: ratioFromPercentage(percentage) },
        {
          onSuccess: () =>
            toast({ title: t('conversionUpdated'), severity: 'success' }),
          onError: (error) =>
            toast({ title: error.message, severity: 'error' }),
        },
      );
    },
    [editMaxConversion, t, toast],
  );

  const onDeleteMaxConversion = useCallback(
    (id: string) => {
      deleteMaxConversion.mutate(
        { id },
        {
          onSuccess: () =>
            toast({ title: t('conversionDeleted'), severity: 'success' }),
          onError: (error) =>
            toast({ title: error.message, severity: 'error' }),
        },
      );
    },
    [deleteMaxConversion, t, toast],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'toExercise',
        header: () => t('toExercise'),
        cell: ({ row }) => row.original.toLibraryExercise.name,
      }),
      columnHelper.display({
        id: 'fromExercise',
        header: () => t('fromExercise'),
        cell: ({ row }) => row.original.fromLibraryExercise.name,
      }),
      columnHelper.accessor('ratio', {
        header: () => t('percentage'),
        cell: ({ row }) => `${ratioToPercentage(row.original.ratio)}%`,
      }),
    ],
    [t],
  );

  const filters = useMemo(
    () => [
      {
        id: 'fromExercise',
        type: 'select' as const,
        title: t('coreExercise'),
        multiselect: true,
        showSearch: true,
        options: exercises.map<FilterOption<LibraryExercise>>((exercise) => ({
          id: exercise.id,
          label: exercise.name,
          value: exercise,
        })),
      },
    ],
    [t, exercises],
  );

  const handleFilterChange = useCallback(
    (filterValues: Record<string, LibraryExercise[]>) => {
      const fromExercise = filterValues['fromExercise'];
      if (fromExercise !== undefined) {
        setFromExerciseFilter(fromExercise);
        setPage(0);
      }
    },
    [setPage],
  );

  const handleResetFilters = useCallback(() => {
    setFromExerciseFilter([]);
    setPage(0);
  }, [setPage]);

  return (
    <Gallery<LibraryExerciseMaxConversion>
      title={t('maxConversions')}
      createButtonText={t('addConversion')}
      createDisabled={!enableCreate}
      createDisabledReason={t('permissionDisabled.create', {
        item: t('maxConversions'),
      })}
      data={data}
      columns={columns}
      filters={filters}
      viewMode={viewMode}
      isLoading={isLoading}
      setViewMode={setViewMode}
      page={page}
      pageSize={pageSize}
      setPage={setPage}
      setPageSize={setPageSize}
      pageSizeOptions={[25, 50, 100]}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      renderCard={(item, viewMode, setSelected) => (
        <MaxConversionCard
          key={item.id}
          item={item}
          viewMode={viewMode}
          onClick={setSelected}
        />
      )}
      renderEditSheet={(selected, setSelected) => (
        <EditExerciseMaxConversionSheet
          exerciseMaxConversion={selected}
          exercises={exercises}
          open={!!selected}
          onOpenChange={() => setSelected(null)}
          onSubmit={onEditMaxConversion}
          onDelete={onDeleteMaxConversion}
        />
      )}
      renderCreateSheet={
        enableCreate
          ? (isOpen, setIsOpen) => (
              <CreateExerciseMaxConversionSheet
                open={isOpen}
                onOpenChange={setIsOpen}
                onSubmit={onCreateMaxConversion}
                exercises={exercises}
              />
            )
          : undefined
      }
    />
  );
}
